import { ApolloError } from "@apollo/client";
import { type MinimalQueryResult } from "~/common/types/typescript";

// https://kentcdodds.com/blog/get-a-catch-block-error-message-with-typescript
type ErrorWithMessage = {
  message: string;
};

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as Record<string, unknown>).message === "string"
  );
}

function toErrorWithMessage(maybeError: unknown): ErrorWithMessage {
  if (isErrorWithMessage(maybeError)) {
    return maybeError;
  }

  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    // fallback for stringify maybeError (with circular references for example)
    return new Error(String(maybeError));
  }
}

/**
 * Parse message from an error object which may or may not be an instanceof Error
 * @param error
 * @returns error message
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof ApolloError) {
    return error.graphQLErrors.length > 0 ? error.graphQLErrors[0].message : error.message;
  }

  return toErrorWithMessage(error).message;
}

/**
 * True when a GraphQL error says the caller's session token is no longer valid.
 *
 * A subgraph answers 401 when the JWT in the session cookie expires, but the
 * gateway wraps that in a 200 response, so the redirect in errorLink never
 * sees it. JSQ subgraphs set no `extensions.code`, so the message text is the
 * only signal. Both gateways keep "401" and "Unauthorized" in that text.
 */
export const isExpiredSessionError = (error: ApolloError | undefined): boolean =>
  error?.graphQLErrors.some(
    (gqlError) => gqlError.message.includes("401") && /unauthorized/i.test(gqlError.message),
  ) ?? false;

export const resultHasErrorWithErrorCode = (
  result: MinimalQueryResult,
  errorCode: string,
): boolean =>
  result.error?.graphQLErrors?.some(
    (gqlError) => gqlError.extensions?.error_code === errorCode,
  ) ?? false;

type ErrorResponse = {
  __typename: "MutationErrorResponse";
  errorCode: string;
  message: string;
};
type Response<T> = T | ErrorResponse;
export function getIsResponseError<T>(data: Response<T>): data is ErrorResponse {
  if (data && typeof data === "object" && "__typename" in data) {
    return data.__typename === "MutationErrorResponse";
  }
  return false;
}
